import React, { Fragment, useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { localeFormattedPrice } from 'common/utils';
import form from 'modules/ticketPublicPage/stores/vouchers/form';
import view from 'modules/ticketPublicPage/stores/vouchers/view';
import facility from 'app/facility';

import WindowEventListener from 'app/components/eventListeners/windowEventListener';
import { useTrapFocus } from 'app/hooks';

import TextField from 'app/commonComponents/TextField';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSelect,
} from './ValuesChoiceDropdown.styles';
import { textFieldLabelStyle } from '../Initial';

const ValuesChoiceDropdown = observer(() => {
  const [showDropDownMenu, setShowDropDownMenu] = useState(false);
  const wrapperRef = useRef(null);
  const dropDownContainerRef = useRef(null);
  const selectRef = useRef(null);
  useTrapFocus(dropDownContainerRef, showDropDownMenu);

  const handleClickOutside = useCallback(
    (event) => {
      if (
        wrapperRef.current &&
        showDropDownMenu &&
        !wrapperRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    },
    [showDropDownMenu]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape' && showDropDownMenu) {
        closeDropdown();
      }
    },
    [showDropDownMenu]
  );

  const closeDropdown = () => {
    selectRef.current?.focus();
    setShowDropDownMenu(false);
  };

  const { activeObject, priceRanges } = view;
  const { min_value: minValue, max_value: maxValue } = activeObject;
  const { settings, languageCode } = facility;
  const { currency, currencyShort } = settings;
  const {
    formValues,
    errors,
    setFormValue,
    setOwnValueClickedStatus,
    isOwnValue,
    validateField,
  } = form;

  return (
    <Fragment>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: '0.5rem',
          gap: '1rem',
        }}
      >
        {priceRanges.length ? (
          <Dropdown ref={wrapperRef}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                fontWeight: 600,
                ...textFieldLabelStyle,
              }}
            >
              {i18n.gettext('Choose a value')}
            </div>
            <DropdownSelect
              ref={selectRef}
              onClick={() => setShowDropDownMenu(!showDropDownMenu)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowDropDownMenu(!showDropDownMenu);
                }
              }}
              $active={showDropDownMenu}
            >
              {isOwnValue
                ? i18n.gettext('Own value')
                : localeFormattedPrice(formValues.get('price'), {
                    languageCode: languageCode,
                    currencyCode: currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
            </DropdownSelect>
            {showDropDownMenu && (
              <DropdownMenu>
                <DropdownContent
                  aria-label={i18n.gettext('Choose a value')}
                  ref={dropDownContainerRef}
                >
                  {activeObject.individual_value_allowed && (
                    <DropdownItem
                      $active={isOwnValue}
                      aria-selected={isOwnValue}
                      onClick={() => {
                        setOwnValueClickedStatus(true);
                        setFormValue('price', minValue);
                        closeDropdown();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setOwnValueClickedStatus(true);
                          setFormValue('price', minValue);
                          closeDropdown();
                        }
                      }}
                    >
                      {i18n.gettext('Own value')}
                    </DropdownItem>
                  )}
                  {priceRanges.map((value, index) => (
                    <DropdownItem
                      key={index}
                      $active={value === formValues.get('price') && !isOwnValue}
                      aria-selected={
                        value === formValues.get('price') && !isOwnValue
                      }
                      onClick={() => {
                        setFormValue('price', value);
                        if (isOwnValue) setOwnValueClickedStatus(false);
                        closeDropdown();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setFormValue('price', value);
                          if (isOwnValue) setOwnValueClickedStatus(false);
                          closeDropdown();
                        }
                      }}
                    >
                      {localeFormattedPrice(value, {
                        languageCode: languageCode,
                        currencyCode: currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </DropdownItem>
                  ))}
                </DropdownContent>
              </DropdownMenu>
            )}
          </Dropdown>
        ) : null}
        <TextField
          autoFocus
          debounce={800}
          label={i18n.sprintf(i18n.gettext('Value (in %s)'), currencyShort)}
          onChange={(e) => {
            setFormValue('price', e.target.value);
            validateField('price');
          }}
          value={formValues.get('price')}
          type="number"
          min={minValue}
          max={maxValue}
          name="price"
          style={{
            width: '100%',
            visibility: isOwnValue ? 'visible' : 'hidden',
          }}
          labelStyle={textFieldLabelStyle}
          error={errors.get('price')}
        />
      </div>
      <WindowEventListener event="mousedown" listener={handleClickOutside} />
      <WindowEventListener event="keydown" listener={handleKeyDown} />
    </Fragment>
  );
});

export default ValuesChoiceDropdown;
